import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseUrl from "../api/utils";

const fmt = (n) =>
  (n || n === 0)
    ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const Gstr3bView = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [fromDate, setFromDate] = useState(firstOfMonth.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/reports/gstr3b-summary`, {
        headers: { Authorization: token },
        params: { from: fromDate, to: toDate },
      });
      setSummary(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/unauthorized");
        return;
      }
      console.error("Failed to load GSTR-3B summary", err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cellStyle = { border: "1px solid #eee", padding: "10px 14px", fontSize: "13px" };
  const thStyle = { ...cellStyle, background: "#f5f6fa", fontWeight: "600", textAlign: "left", color: "#555" };

  return (
    <div style={{ background: "#f5f6fa", padding: "20px", borderRadius: "8px" }}>
      {/* ── Date range picker ── */}
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", marginBottom: "20px" }}>
        <div>
          <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }}
          />
        </div>
        <div>
          <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }}
          />
        </div>
        <button className="btn btn-primary btn-sm" onClick={fetchSummary} disabled={loading}>
          {loading ? "Loading..." : "Apply"}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>Loading GSTR-3B summary...</div>
      ) : !summary ? null : (
        <>
          {/* ── Net Payable Highlight ── */}
          <div style={{
            background: "#fff", borderRadius: "10px", padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px",
            borderTop: "4px solid #c0392b", textAlign: "center"
          }}>
            <div style={{ fontSize: "12px", color: "#999", fontWeight: "700", textTransform: "uppercase" }}>
              Net Tax Payable
            </div>
            <div style={{ fontSize: "32px", fontWeight: "700", color: "#c0392b", marginTop: "8px" }}>
              ₹{fmt(summary.totalNetPayable)}
            </div>
          </div>

          {/* ── Output Tax vs ITC breakdown ── */}
          <div style={{ background: "#fff", borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px" }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#333", marginBottom: "12px" }}>
              Output Tax (Sales) − Input Tax Credit (Purchases) = Net Payable
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Tax Type</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Output Tax (Sales)</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>ITC (Purchases)</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Net Payable</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...cellStyle, fontWeight: "600" }}>CGST</td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(summary.outputCgst)}</td>
                  <td style={{ ...cellStyle, textAlign: "right", color: "#27ae60" }}>− ₹{fmt(summary.itcCgst)}</td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.netCgstPayable)}</td>
                </tr>
                <tr>
                  <td style={{ ...cellStyle, fontWeight: "600" }}>SGST</td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(summary.outputSgst)}</td>
                  <td style={{ ...cellStyle, textAlign: "right", color: "#27ae60" }}>− ₹{fmt(summary.itcSgst)}</td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.netSgstPayable)}</td>
                </tr>
                <tr>
                  <td style={{ ...cellStyle, fontWeight: "600" }}>IGST</td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(summary.outputIgst)}</td>
                  <td style={{ ...cellStyle, textAlign: "right", color: "#27ae60" }}>− ₹{fmt(summary.itcIgst)}</td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.netIgstPayable)}</td>
                </tr>
                <tr style={{ background: "#f5f6fa" }}>
                  <td style={{ ...cellStyle, fontWeight: "700" }}>TOTAL</td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>
                    ₹{fmt(summary.outputCgst + summary.outputSgst + summary.outputIgst)}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700", color: "#27ae60" }}>
                    − ₹{fmt(summary.itcCgst + summary.itcSgst + summary.itcIgst)}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>
                    ₹{fmt(summary.totalNetPayable)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Turnover summary ── */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
            <div style={{ flex: 1, background: "#fff", borderRadius: "10px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: "11px", color: "#999", fontWeight: "700", textTransform: "uppercase" }}>Total Taxable Sales</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#1976d2", marginTop: "6px" }}>₹{fmt(summary.totalTaxableSales)}</div>
            </div>
            <div style={{ flex: 1, background: "#fff", borderRadius: "10px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: "11px", color: "#999", fontWeight: "700", textTransform: "uppercase" }}>Total Taxable Purchases</div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#1976d2", marginTop: "6px" }}>₹{fmt(summary.totalTaxablePurchases)}</div>
            </div>
          </div>

          <div style={{ fontSize: "12px", color: "#888", background: "#fffbea", border: "1px solid #f0e5b8", borderRadius: "8px", padding: "12px 16px" }}>
            <i className="fa-solid fa-circle-info mr-1"></i>
            This is a reference calculation for GSTR-3B. ITC is only counted for purchases marked "ITC Eligible". Always cross-check before filing.
          </div>
        </>
      )}
    </div>
  );
};

export default Gstr3bView;